import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation, useParams } from 'react-router';
import { toast } from 'react-toastify';

import {
  Card,
  Loader,
  Select,
} from '~/components';
import { buildTabLink, IntegrInlineTabs } from '~/pages/Analysis/Integrations/IntegrLayout';
import { IntegrPerfilContents } from '~/pages/Analysis/Integrations/IntegrPerfil/index';
import { IntegrRealTimeContents } from '~/pages/Analysis/Integrations/IntegrRealTime/index';
import { TUnitInfo, UnitLayout } from '~/pages/Analysis/Units/UnitLayout';
import { apiCall, ApiParams, ApiResps } from '~/providers';
import { CoolAutomationHist } from '../../Integrations/IntegrHistory/CoolAutomationHist';
import { IntegrDevInfo } from '../../Integrations/IntegrPerfil/IntegrDevInfo';
import { CoolAutomationEdit } from '../../Integrations/IntegrEdit/CoolAutomationEdit';
import { withTransaction } from '@elastic/apm-rum-react';
import { generateNameFormatted } from '~/helpers/titleHelper';
import {
  pickInitialDevice, sortDevicesByDataSource, VRF_DEVICE_QUERY_PARAM, VrfDevice,
} from './helpers/vrfDevices';

const UNIT_STATUS_TO_INCLUDE: ApiParams['/get-integrations-list']['INCLUDE_UNIT_STATUS'] = ['DEACTIVATED', 'FORCED_UNINSTALL', 'INSTALLATION', 'OPERATION', 'MODERNIZATION'];

interface SettledDeviceInfo {
  requestKey: string;
  resp: ApiResps['/get-integration-info'] | null;
}

export const UnitVRF = (): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const routeParams = useParams<{ unitId: string }>();
  const unitId = Number(routeParams.unitId);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [unitInfo, setUnitInfo] = useState<TUnitInfo | null>(null);
  const [devicesList, setDevicesList] = useState<VrfDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<VrfDevice | null>(null);
  const [settledDeviceInfo, setSettledDeviceInfo] = useState<SettledDeviceInfo | null>(null);

  const queryPars = queryString.parse(location.search);
  const aba = queryPars.aba?.toString() || '';
  const preferredIntegrId = queryPars[VRF_DEVICE_QUERY_PARAM]?.toString() || null;
  const isRealTimeTab = aba === 'tempo-real' || aba === '';
  const hasMultipleDevices = devicesList.length > 1;
  const selectedIntegrId = selectedDevice?.integrId ?? null;
  const deviceRequestKey = selectedIntegrId ? `${selectedIntegrId}|${aba}` : null;
  // Derivados da requisição atual (DVI + aba): já no render em que o DVI ou a aba mudam a tela
  // entra em carregamento e a resposta anterior deixa de valer, sem exibir dados de outro DVI.
  const isLoadingDevice = deviceRequestKey !== null && settledDeviceInfo?.requestKey !== deviceRequestKey;
  const devInfoResp = isLoadingDevice ? null : (settledDeviceInfo?.resp ?? null);

  function renderTabContent(): ReactElement | null {
    if (!selectedDevice) return null;
    // Os conteúdos das abas só buscam dados na montagem; a key pelo integrId força a
    // remontagem ao trocar de DVI, para que exibam e comandem o DVI selecionado.
    const deviceKey = selectedDevice.integrId;

    if (aba === 'historico') {
      return <CoolAutomationHist key={deviceKey} deviceId={selectedDevice.integrId} />;
    }

    if (!devInfoResp) return null;

    if (aba === 'perfil') {
      return (
        <div style={{ paddingBottom: '30px' }}>
          <IntegrPerfilContents
            key={deviceKey}
            integrType="coolautomation"
            integrId={selectedDevice.dataSource}
            devInfoResp={devInfoResp}
            editLink={buildTabLink('editar', history)}
          />
        </div>
      );
    }

    if (aba === 'editar') {
      return (
        <div key={deviceKey} style={{ paddingBottom: '30px' }}>
          <div style={{ paddingTop: '20px' }}>
            <Card>
              <div style={{ fontWeight: 'bold', paddingBottom: '35px', fontSize: '1.25em' }}>
                {t('informacoes')}
              </div>
              <IntegrDevInfo devInfo={devInfoResp.info} />
              <br />
              <br />
              <CoolAutomationEdit schedInfo={devInfoResp.coolautomation} />
            </Card>
          </div>
        </div>
      );
    }

    if (isRealTimeTab) {
      return (
        <IntegrRealTimeContents
          key={deviceKey}
          integrType="coolautomation"
          integrId={selectedDevice.dataSource}
          devInfoResp={devInfoResp}
        />
      );
    }

    return null;
  }

  useEffect(() => {
    async function loadUnitAndDevices(): Promise<void> {
      try {
        setIsLoading(true);
        const [
          unitInfoResp,
          { list },
        ] = await Promise.all([
          apiCall('/clients/get-unit-info', { unitId }),
          apiCall('/get-integrations-list', { supplier: 'coolautomation', unitIds: [unitId], INCLUDE_UNIT_STATUS: UNIT_STATUS_TO_INCLUDE }),
        ]);
        const sortedDevices = sortDevicesByDataSource(list);
        setUnitInfo(unitInfoResp);
        setDevicesList(sortedDevices);
        setSelectedDevice(pickInitialDevice(sortedDevices, preferredIntegrId));
      } catch (err) {
        console.log(err);
        toast.error(t('houveErro'));
      }
      setIsLoading(false);
    }

    loadUnitAndDevices();
  }, [unitId, preferredIntegrId, t]);

  useEffect(() => {
    if (!selectedIntegrId) return undefined;
    // Evita que a resposta de um DVI anterior sobrescreva a do DVI selecionado agora.
    let isCurrentRequest = true;
    const requestKey = `${selectedIntegrId}|${aba}`;

    async function loadDeviceInfo(integrId: string): Promise<void> {
      try {
        const response = await apiCall('/get-integration-info', { supplier: 'coolautomation', integrId });
        if (isCurrentRequest) setSettledDeviceInfo({ requestKey, resp: response });
      } catch (err) {
        console.log(err);
        if (isCurrentRequest) {
          toast.error(t('houveErro'));
          setSettledDeviceInfo({ requestKey, resp: null });
        }
      }
    }

    loadDeviceInfo(selectedIntegrId);

    return () => {
      isCurrentRequest = false;
    };
  }, [selectedIntegrId, aba, t]);

  return (
    <>
      <Helmet>
        <title>{generateNameFormatted(unitInfo?.UNIT_NAME, t('sistemasVRF'))}</title>
      </Helmet>
      <UnitLayout unitInfo={unitInfo} hideUnitTabs />
      {isLoading ? <Loader /> : (
        <div style={{ paddingTop: '10px' }}>
          {isLoadingDevice ? <Loader /> : (
            <div>
              <div style={{ paddingTop: '10px', paddingBottom: '20px' }}>
                <IntegrInlineTabs integrType="coolautomation" />
              </div>
              {hasMultipleDevices ? (
                <SelectDeviceVRF
                  devicesList={devicesList}
                  selectedDevice={selectedDevice}
                  onSelectDevice={setSelectedDevice}
                />
              ) : null}
              {renderTabContent()}
            </div>
          )}
        </div>
      )}
    </>
  );
};

interface SelectDeviceVRFProps {
  devicesList: VrfDevice[];
  selectedDevice: VrfDevice | null;
  onSelectDevice: (device: VrfDevice) => void;
}

export const SelectDeviceVRF = ({
  devicesList,
  selectedDevice,
  onSelectDevice,
}: Readonly<SelectDeviceVRFProps>): ReactElement => {
  const { t } = useTranslation();

  return (
    <div style={{ maxWidth: '400px', paddingTop: '20px', paddingBottom: '20px' }}>
      <Select
        options={devicesList}
        value={selectedDevice}
        placeholder={t('dispositivo')}
        propLabel="dataSource"
        onSelect={(opt: VrfDevice) => onSelectDevice(opt)}
        notNull
      />
    </div>
  );
};

export default withTransaction('UnitVRF', 'component')(UnitVRF);
